import React, { FC, useState } from 'react'
import { Loading } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { DateRangeView, Alert } from '@pandiafi/components'
import { DateTime } from 'luxon'
import { useSearchParams } from 'react-router-dom'

import { ReservationCalendarsDataLoader } from './ReservationCalendarsDataLoader'
import { ReservationCalendarsWithLoading } from './ReservationCalendarsWithLoading'
import { useAPIGetContract } from '../../api/apiHooks/useAPIGetContract'
import { useCurrentContract } from '../../hooks/useCurrentContract/useCurrentContract'
import { PaymentStatus } from './PaymentStatus'
import type { ReservationCalendarForResident } from './types'
import { UserMetadata } from '../../../common/validations'
import { ErrorHandler } from './ErrorHandler'

const filterCalendarsByDateRange = (
  calendars: ReservationCalendarForResident[],
  startDate: string,
  endDate: string
) => {
  const rangeStart = DateTime.fromISO(startDate).startOf('day')
  const rangeEnd = DateTime.fromISO(endDate).endOf('day')

  return calendars.filter((calendar) => {
    const validFrom = DateTime.fromISO(calendar.validFrom).startOf('day')
    const validUntil = calendar.validUntil
      ? DateTime.fromISO(calendar.validUntil).endOf('day')
      : null

    const startsBeforeRangeEnds = validFrom <= rangeEnd
    const endsAfterRangeStarts = !validUntil || validUntil >= rangeStart

    return startsBeforeRangeEnds && endsAfterRangeStarts
  })
}

interface ReservationCalendarsProps {
  reservableCategory?: string
}

export const ReservationCalendars: FC<ReservationCalendarsProps> = ({
  reservableCategory,
}) => {
  const { t } = useTranslation(['reservations', 'errors'])
  const [searchParams] = useSearchParams()
  const { currentContractId } = useCurrentContract()
  const {
    data: contract,
    isLoading: isContractLoading,
    isError: isContractError,
  } = useAPIGetContract(currentContractId)

  const [startDate, setStartDate] = useState<string>(() => {
    const today = DateTime.now()
    const weekStart = today.startOf('week')
    const startDateStr = weekStart.toISODate()
    return startDateStr
  })
  const [endDate, setEndDate] = useState<string>(() => {
    const today = DateTime.now()
    const weekEnd = today.startOf('week').plus({ days: 6 })
    const endDateStr = weekEnd.toISODate()
    return endDateStr
  })

  const calendarMaxDate = DateTime.now()
    .plus({ weeks: 4 })
    .endOf('week')
    .toISODate()
  const calendarMinDate = DateTime.now().startOf('week').toISODate()

  const handleDateRangeChange = (
    _event: unknown,
    newStart: string,
    newEnd: string
  ) => {
    setStartDate(newStart || startDate)
    setEndDate(newEnd || endDate)
  }

  const resetDateRangeToDefault = () => {
    const today = DateTime.now()
    const weekStart = today.startOf('week')
    const weekEnd = today.startOf('week').plus({ days: 6 })
    setStartDate(weekStart.toISODate())
    setEndDate(weekEnd.toISODate())
  }

  if (isContractLoading) {
    return <Loading variant="details" />
  }

  if (isContractError || !contract) {
    return (
      <Alert variant="warning" className="my-8">
        {t('errors:other.internalError')}
      </Alert>
    )
  }

  return (
    <ErrorHandler
      reservableCategory={reservableCategory}
      resetView={resetDateRangeToDefault}
    >
      <div className="space-y-6 max-w-full">
        <DateRangeView
          startDate={startDate}
          endDate={endDate}
          maxDate={calendarMaxDate}
          minDate={calendarMinDate}
          onChange={handleDateRangeChange}
          largeSquareBtn
          className="sticky top-16 bg-white py-1 z-20"
        >
          <div className="mx-1">
            <React.Suspense fallback={<Loading variant="details" />}>
              <ReservationCalendarsDataLoader
                reservableCategory={reservableCategory}
                weekStartDate={startDate}
                refreshKey={[startDate, endDate]}
              >
                {(
                  calendars: ReservationCalendarForResident[],
                  isLoading: boolean,
                  userMetadata: UserMetadata
                ) => {
                  const visibleCalendars = filterCalendarsByDateRange(
                    calendars,
                    startDate,
                    endDate
                  )

                  if (isLoading) {
                    return <Loading variant="details" />
                  }

                  if (visibleCalendars.length === 0) {
                    return (
                      <Alert variant="info" className="my-8">
                        {t('reservations:noCalendarsAvailable')}
                      </Alert>
                    )
                  }

                  return (
                    <ReservationCalendarsWithLoading
                      calendars={visibleCalendars}
                      startDate={startDate}
                      endDate={endDate}
                      contract={contract}
                      readonly={userMetadata.isImpersonator}
                    />
                  )
                }}
              </ReservationCalendarsDataLoader>
            </React.Suspense>
          </div>
        </DateRangeView>
      </div>
      <React.Suspense fallback={<Loading variant="details" />}>
        {searchParams.get('status') === 'success' && (
          <PaymentStatus status="success" />
        )}
        {searchParams.get('status') === 'failed' && (
          <PaymentStatus status="failed" />
        )}
      </React.Suspense>
    </ErrorHandler>
  )
}
